import type { SearchResponse, Vector } from '../../contracts/src/search.js';
import { SearchService } from './search.service.js';

export interface RecallProbeCandidate {
  readonly text: string;
  readonly vector: Vector;
}

export interface RecallProbeResult extends RecallProbeCandidate {
  readonly approximate: SearchResponse;
  readonly exact: SearchResponse;
  readonly recall: number;
}

export interface RecallProbeOptions {
  readonly k: number;
  readonly efSearch: number;
}

export class RecallProbeService {
  readonly #searchService: SearchService;
  #sequence = 0;

  constructor(searchService: SearchService) {
    this.#searchService = searchService;
  }

  async findWeakest(
    candidates: readonly RecallProbeCandidate[],
    options: RecallProbeOptions
  ): Promise<RecallProbeResult> {
    if (candidates.length === 0) throw new Error('No truth-challenge queries were supplied.');
    let weakest: RecallProbeResult | null = null;

    for (const candidate of candidates) {
      const sequence = ++this.#sequence;
      const approximate = await this.#searchService.search({
        requestId: `probe-fast-${sequence}`,
        vector: candidate.vector,
        k: options.k,
        efSearch: options.efSearch,
        exact: false,
        queryKind: 'text',
        queryText: candidate.text
      });
      const exact = await this.#searchService.search({
        requestId: `probe-truth-${sequence}`,
        vector: candidate.vector,
        k: options.k,
        efSearch: options.efSearch,
        exact: true,
        queryKind: 'text',
        queryText: candidate.text
      });
      const recall = calculateRecall(approximate, exact);
      const result = { ...candidate, approximate, exact, recall };
      if (!weakest || isWeaker(result, weakest)) weakest = result;
      if (recall <= .625) break;
    }

    if (!weakest) throw new Error('Truth challenge did not produce a result.');
    return weakest;
  }
}

function calculateRecall(approximate: SearchResponse, exact: SearchResponse): number {
  if (exact.hits.length === 0) return 1;
  const fastIds = new Set(approximate.hits.map((hit) => hit.id));
  return exact.hits.filter((hit) => fastIds.has(hit.id)).length / exact.hits.length;
}

function isWeaker(left: RecallProbeResult, right: RecallProbeResult): boolean {
  if (left.recall !== right.recall) return left.recall < right.recall;
  if (left.approximate.inspectedRatio !== right.approximate.inspectedRatio) {
    return left.approximate.inspectedRatio < right.approximate.inspectedRatio;
  }
  return left.approximate.trace.distanceCalculations < right.approximate.trace.distanceCalculations;
}
